import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Logger, LoggerFactory } from './logger.service';
import { IdentityService } from './identity/identity.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);
  private readonly logger: Logger = inject(LoggerFactory).get('AuthInterceptor');
  private isRefreshing = false;
  private lastRefreshAt = 0;
  private readonly REFRESH_COOLDOWN_MS = 2000;
  private readonly refreshResult$ = new BehaviorSubject<boolean | null>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          !this.isAuthEndpoint(request) &&
          this.shouldAttemptRefresh()
        ) {
          return this.handle401(request, next);
        }
        return throwError(err);
      }),
    );
  }

  private isAuthEndpoint(req: HttpRequest<unknown>): boolean {
    const url = req.url || '';
    return url.includes('jwt/update-payload') || url.includes('/account/login') || url.includes('/account/register');
  }

  private shouldAttemptRefresh(): boolean {
    return Date.now() - this.lastRefreshAt > this.REFRESH_COOLDOWN_MS;
  }

  private handle401(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshResult$.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((success) => {
          if (success) {
            return next.handle(this.cloneWithFreshAuth(request));
          }
          return throwError(
            new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized (refresh failed while queued)',
              url: request.url,
            }),
          );
        }),
      );
    }

    this.isRefreshing = true;
    this.refreshResult$.next(null);
    this.logger.warn(`401 on ${request.method} ${request.url} — JWT expired, attempting silent refresh`);

    const identityService = this.injector.get(IdentityService);

    return from(identityService.requestUpdateIdentityPayloadAsync()).pipe(
      switchMap((success: boolean) => {
        this.isRefreshing = false;
        this.refreshResult$.next(success);
        if (success) {
          this.lastRefreshAt = Date.now();
          this.logger.info(`JWT refresh succeeded; retrying ${request.method} ${request.url}`);
          return next.handle(this.cloneWithFreshAuth(request));
        }
        this.logger.warn(`JWT refresh failed; propagating 401 for ${request.method} ${request.url}`);
        return throwError(
          new HttpErrorResponse({
            status: 401,
            statusText: 'Unauthorized (refresh failed)',
            url: request.url,
          }),
        );
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.refreshResult$.next(false);
        this.logger.error(`JWT refresh threw unexpectedly: ${err?.message ?? err}`);
        return throwError(err);
      }),
    );
  }

  private cloneWithFreshAuth(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request;
  }
}
